import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Award } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/app/components/ui/table';
import { useAllTechCertifications } from '@/hooks/use-crew';
import type { TechCertStatus } from '@/lib/types';
import { formatDateOnly } from '@/lib/date-utils';

const certStatusColors: Record<TechCertStatus, string> = {
  valid: 'bg-green-100 text-green-800',
  expiring_soon: 'bg-yellow-100 text-yellow-800',
  expired: 'bg-red-100 text-red-800',
  revoked: 'bg-gray-100 text-gray-800',
};

const certStatusLabels: Record<TechCertStatus, string> = {
  valid: 'Valid',
  expiring_soon: 'Expiring Soon',
  expired: 'Expired',
  revoked: 'Revoked',
};

export function ExpiringCertsPage() {
  const [statusFilter, setStatusFilter] = useState<'expiring_soon' | 'expired' | ''>('');
  const [page, setPage] = useState(1);

  const { data: expiringCerts, isLoading: loadingExpiring } = useAllTechCertifications({
    status: 'expiring_soon',
  });

  const { data: expiredCerts, isLoading: loadingExpired } = useAllTechCertifications({
    status: 'expired',
  });

  const isLoading = loadingExpiring || loadingExpired;
  const expiringCount = expiringCerts?.count ?? 0;
  const expiredCount = expiredCerts?.count ?? 0;
  const totalCount = expiringCount + expiredCount;
  const allCerts = [
    ...(expiringCerts?.results ?? []),
    ...(expiredCerts?.results ?? []),
  ];

  const filteredCerts = statusFilter
    ? allCerts.filter((c) => c.status === statusFilter)
    : allCerts;

  const pageSize = 20;
  const totalPages = Math.ceil(filteredCerts.length / pageSize);
  const pagedCerts = filteredCerts.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="icon">
          <Link to="/crew">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-6 w-6 text-orange-500" />
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Expiring Certifications</h1>
          </div>
          <p className="text-gray-600 mt-1">
            {totalCount} certification{totalCount !== 1 ? 's' : ''} requiring attention
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card
          className={`cursor-pointer transition-shadow hover:shadow-md ${!statusFilter ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => { setStatusFilter(''); setPage(1); }}
        >
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold">{totalCount}</p>
            <p className="text-sm text-gray-600">All Issues</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-shadow hover:shadow-md ${statusFilter === 'expiring_soon' ? 'ring-2 ring-yellow-500' : ''}`}
          onClick={() => { setStatusFilter('expiring_soon'); setPage(1); }}
        >
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{expiringCount}</p>
            <p className="text-sm text-gray-600">Expiring Soon</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-shadow hover:shadow-md ${statusFilter === 'expired' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => { setStatusFilter('expired'); setPage(1); }}
        >
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{expiredCount}</p>
            <p className="text-sm text-gray-600">Expired</p>
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : pagedCerts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Award className="h-12 w-12 text-green-400 mb-4" />
            <p className="text-gray-600">All certifications are up to date!</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Technician</TableHead>
                  <TableHead>Certification</TableHead>
                  <TableHead className="hidden sm:table-cell">Cert Number</TableHead>
                  <TableHead>Expiry Date</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedCerts.map((cert) => (
                  <TableRow key={cert.id}>
                    <TableCell>
                      <Link
                        to={`/crew/techs/${cert.tech}`}
                        className="text-blue-600 hover:underline font-medium"
                      >
                        {cert.tech_name ?? `Tech #${cert.tech}`}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{cert.cert_name}</p>
                        <p className="text-xs text-gray-500">{cert.issuing_body}</p>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell text-gray-600">
                      {cert.cert_number || '-'}
                    </TableCell>
                    <TableCell className="text-gray-600">
                      {formatDateOnly(cert.expiry_date, 'N/A')}
                    </TableCell>
                    <TableCell>
                      <Badge className={certStatusColors[cert.status]}>
                        {certStatusLabels[cert.status]}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
